import {
  reservationGuideAutoOpen,
  type TReservationGuideAutoOpen,
} from '@/domain/reservations/reservation-guide';
import type { IReservationsSettingsSummary } from '@/interfaces/IReservation';

/** Branch keys (`restaurantId:branchId`) whose finish screen this device already showed. */
const STORAGE_KEY = 'reservations-guide-shown';

function readShown(): string[] {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    const parsed: unknown = raw ? JSON.parse(raw) : [];
    return Array.isArray(parsed)
      ? parsed.filter((value): value is string => typeof value === 'string')
      : [];
  } catch {
    return [];
  }
}

/** The finish screen of an active branch was seen on this device: no more automatic landing. */
export function markReservationGuideShown(key: string): void {
  markShown(key);
}

function markShown(key: string): void {
  try {
    const list = readShown();
    if (list.includes(key)) return;
    localStorage.setItem(STORAGE_KEY, JSON.stringify([...list, key]));
  } catch {
    // No storage: the finish screen simply opens again on the next visit.
  }
}

export interface IReservationGuideAutoOpen {
  /**
   * Decides once per branch and page visit whether opening Reservas lands on
   * the guide (D23); true means "open it now".
   */
  claim(
    scopeKey: string,
    summary: Pick<IReservationsSettingsSummary, 'enabled' | 'hasReservations'>,
    incomplete: boolean
  ): boolean;
}

export function useReservationGuideAutoOpen(): IReservationGuideAutoOpen {
  const decided = new Set<string>();
  function claim(
    scopeKey: string,
    summary: Pick<IReservationsSettingsSummary, 'enabled' | 'hasReservations'>,
    incomplete: boolean
  ): boolean {
    if (decided.has(scopeKey)) return false;
    decided.add(scopeKey);
    const reason: TReservationGuideAutoOpen = reservationGuideAutoOpen(
      summary,
      incomplete,
      readShown().includes(scopeKey)
    );
    if (reason === 'unused') markShown(scopeKey);
    return reason !== null;
  }
  return { claim };
}
